/**
 * Persistência do recorte do cabeçalho — empresa e período (SPEC-017, decisão 11).
 *
 * Único lugar do projeto que lê/grava esta chave, no molde de `@/auth/session-store`.
 *
 * NÃO é dado sensível: é a escolha de tela da pessoa. Mesmo assim mora em
 * `sessionStorage` e não em `localStorage`, porque a decisão 11 diz "vale até a pessoa
 * trocar o período ou SAIR DO SISTEMA, e continua ao recarregar a página" — e é isso que
 * `sessionStorage` faz: sobrevive ao F5, morre com a aba.
 */

import { getPeriodoUltimosNDias } from '@/lib/utils/formatters';

export interface RecorteDoCabecalho {
  empresaId: string | null;
  empresaCnpjs: string | null;
  dataInicio: string | null;
  dataFim: string | null;
  periodoModo: 'mesano' | 'periodo';
  periodoMes: string | null;
  periodoAno: number | null;
  /**
   * Houve um "Buscar" com este recorte.
   *
   * É ELE que liga a decisão 11, e não a simples existência de um recorte: enquanto a
   * pessoa escolhe empresa e período no cabeçalho, os valores mudam a cada clique, e abrir
   * tela por tela buscando a cada mudança seria uma consulta por clique. Depois do primeiro
   * Buscar, as outras telas abrem já buscadas — que é o que a decisão pede.
   */
  aplicado: boolean;
}

const CHAVE = 'tp_recorte_cabecalho';

export function recortePadrao(): RecorteDoCabecalho {
  const periodo = getPeriodoUltimosNDias(30);
  return {
    empresaId: null,
    empresaCnpjs: null,
    dataInicio: periodo.dataInicio,
    dataFim: periodo.dataFim,
    periodoModo: 'periodo',
    periodoMes: null,
    periodoAno: null,
    aplicado: false,
  };
}

/**
 * O recorte guardado, ou o padrão.
 *
 * <b>A FORMA é conferida, não suposta.</b> O que está no storage foi escrito por uma
 * versão anterior do app e sobrevive a deploy: um `periodoModo` que deixou de existir, ou
 * um campo que virou outro tipo, entraria aqui e viajaria para as cinco telas como se
 * fosse escolha da pessoa — e o sintoma seria consulta vazia em todo lugar, sem erro.
 * Qualquer coisa fora do contrato é descartada inteira, e o padrão vale.
 *
 * Ler storage também PODE LANÇAR (aba anônima, cookies bloqueados): por isso o
 * `try/catch`, e por isso ele devolve o padrão em vez de deixar o app sem cabeçalho.
 */
export function recorteGuardado(): RecorteDoCabecalho {
  try {
    const cru = sessionStorage.getItem(CHAVE);
    if (!cru) return recortePadrao();

    const lido: unknown = JSON.parse(cru);
    return ehRecorte(lido) ? lido : recortePadrao();
  } catch {
    return recortePadrao();
  }
}

export function guardarRecorte(recorte: RecorteDoCabecalho): void {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify(recorte));
  } catch {
    /* Storage indisponível: o recorte segue valendo na memória da página. */
  }
}

export function limparRecorte(): void {
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    /* Nada a limpar se nem dá para ler. */
  }
}

function textoOuNulo(v: unknown): boolean {
  return v === null || typeof v === 'string';
}

function ehRecorte(v: unknown): v is RecorteDoCabecalho {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;

  return (
    textoOuNulo(r.empresaId) &&
    textoOuNulo(r.empresaCnpjs) &&
    textoOuNulo(r.dataInicio) &&
    textoOuNulo(r.dataFim) &&
    (r.periodoModo === 'mesano' || r.periodoModo === 'periodo') &&
    textoOuNulo(r.periodoMes) &&
    (r.periodoAno === null || typeof r.periodoAno === 'number') &&
    typeof r.aplicado === 'boolean'
  );
}
